import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest, ApiError } from "src/services/apiClient";
import { Patient } from "src/types";
import { isBirthDateValid } from "src/helpers/birthDateBounds";
import type { ToastState } from "src/components/Toast/Toast";
import { isDemoMode } from "src/demo/demoApi";
import { normalizeSalutationCode } from "src/helpers/salutation";
import { normalizeGenderCode } from "src/helpers/gender";

type PatientFormFields = {
    firstName: string;
    lastName: string;
    salutation: string;
    title: string;
    gender: string;
    birthDate: string;
    insuranceProvider: string;
    insuranceNumber: string;
    externalId: string;
};

/** PatID can be typed in unless the practice-software sync set it (it's that sync's key). */
export const canEditExternalId = (p: Patient | null | undefined): boolean => !!p && p.source !== "gdt_sync";

function readFields(p: Patient): PatientFormFields {
    return {
        firstName: p.firstName ?? "",
        lastName: p.lastName ?? "",
        salutation: normalizeSalutationCode(p.salutation as any),
        title: p.title ?? "",
        gender: normalizeGenderCode(p.gender),
        birthDate: p.birthDate ? new Date(p.birthDate).toISOString().split("T")[0] : "",
        insuranceProvider: p.insuranceProvider ?? "",
        insuranceNumber: p.insuranceNumber ?? "",
        externalId: p.externalId ?? "",
    };
}

const PATIENT_ONLY_FIELDS = ["gender", "insuranceProvider", "insuranceNumber", "externalId"] as const;

function fieldsDiffer(a: PatientFormFields, b: PatientFormFields): boolean {
    return (Object.keys(a) as (keyof PatientFormFields)[]).some((k) => a[k] !== b[k]);
}

/** `patientOnly`: the recipient's own patient record (same person), edited on the recipient card.
 *  Name, salutation, title and birth date come from the recipient form there (the backend copies
 *  them across), so only the fields a recipient doesn't have are saved — never stale copies. */
export function usePatientForm(
    patient: Patient | null,
    onToast: (state: ToastState | null) => void,
    onSaved?: () => void | Promise<void>,
    { patientOnly = false }: { patientOnly?: boolean } = {},
) {
    const { t } = useTranslation();

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [salutation, setSalutation] = useState("H");
    const [title, setTitle] = useState("");
    const [gender, setGender] = useState("");
    const [birthDate, setBirthDate] = useState("");
    const [insuranceProvider, setInsuranceProvider] = useState("");
    const [insuranceNumber, setInsuranceNumber] = useState("");
    const [externalId, setExternalId] = useState("");
    const [saving, setSaving] = useState(false);

    const syncedRef = useRef<PatientFormFields | null>(null);
    const syncedIdRef = useRef<string | null>(null);
    // Bumped whenever syncedRef moves (load or save) — a ref alone doesn't make isDirty recompute.
    const [syncVersion, setSyncVersion] = useState(0);

    const currentFields = useMemo<PatientFormFields>(
        () => ({ firstName, lastName, salutation, title, gender, birthDate, insuranceProvider, insuranceNumber, externalId }),
        [firstName, lastName, salutation, title, gender, birthDate, insuranceProvider, insuranceNumber, externalId],
    );
    const currentFieldsRef = useRef(currentFields);
    currentFieldsRef.current = currentFields;

    const isDirty = useMemo(() => {
        const base = syncedRef.current;
        if (!base) return false;
        if (!patientOnly) return fieldsDiffer(currentFields, base);
        return PATIENT_ONLY_FIELDS.some((k) => currentFields[k] !== base[k]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFields, patientOnly, syncVersion]);

    useEffect(() => {
        if (!patient?.id) {
            syncedRef.current = null;
            syncedIdRef.current = null;
            return;
        }
        if (syncedIdRef.current && syncedIdRef.current !== patient.id) {
            syncedRef.current = null;
            syncedIdRef.current = null;
        }
        if (syncedRef.current && fieldsDiffer(currentFieldsRef.current, syncedRef.current)) return;
        const fields = readFields(patient);
        setFirstName(fields.firstName);
        setLastName(fields.lastName);
        setSalutation(fields.salutation);
        setTitle(fields.title);
        setGender(fields.gender);
        setBirthDate(fields.birthDate);
        setInsuranceProvider(fields.insuranceProvider);
        setInsuranceNumber(fields.insuranceNumber);
        setExternalId(fields.externalId);
        syncedRef.current = fields;
        syncedIdRef.current = patient.id;
        setSyncVersion((v) => v + 1);
    }, [patient]);

    const handleSave = useCallback(async () => {
        if (!patient?.id) return;
        if (!patientOnly && !lastName) {
            onToast({ message: t("recipientPage.validationError"), variant: "error" });
            return;
        }
        if (!patientOnly && birthDate && !isBirthDateValid(birthDate)) {
            onToast({ message: t("form.birthDateInvalid"), variant: "error" });
            return;
        }
        setSaving(true);
        try {
            await apiRequest(
                `/patients/${patient.id}`,
                {
                    method: "PATCH",
                    body: JSON.stringify({
                        ...(patientOnly ? {} : {
                            firstName,
                            lastName,
                            salutation,
                            title,
                            birthDate: birthDate || undefined,
                        }),
                        gender: gender || undefined,
                        insuranceProvider: insuranceProvider || undefined,
                        insuranceNumber: insuranceNumber || undefined,
                        // Only sent when it changed and may be edited — "" clears it.
                        ...(canEditExternalId(patient) && externalId !== (syncedRef.current?.externalId ?? "")
                            ? { externalId }
                            : {}),
                    }),
                },
                { "Content-Type": "application/json" },
            );
            onToast({
                message: isDemoMode() ? t("common.demoSimulated") : t("common.changesSaved"),
                variant: "success",
            });
            syncedRef.current = { ...currentFields };
            setSyncVersion((v) => v + 1);
            await onSaved?.();
        } catch (err) {
            const code = err instanceof ApiError ? err.body?.code : undefined;
            onToast({ message: code === "EXTERNAL_ID_TAKEN" ? t("form.externalIdTaken") : t("common.saveFailed"), variant: "error" });
        } finally {
            setSaving(false);
        }
    }, [patient, patientOnly, firstName, lastName, salutation, title, gender, birthDate, insuranceProvider, insuranceNumber, externalId, currentFields, onToast, onSaved, t]);

    return {
        firstName, setFirstName,
        lastName, setLastName,
        salutation, setSalutation,
        title, setTitle,
        gender, setGender,
        birthDate, setBirthDate,
        insuranceProvider, setInsuranceProvider,
        insuranceNumber, setInsuranceNumber,
        externalId, setExternalId,
        isDirty,
        saving,
        handleSave,
    };
}
